import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowDown, ArrowRight, Check, CheckCircle2, FileCheck2, FileSearch,
    FolderCheck, ListChecks, MessageCircle, ShieldCheck
} from 'lucide-react';
import './landing-premium.css';

const VIDEO_SRC = '/media/kreo-seace-world.mp4';
const POSTER_SRC = '/media/kreo-seace-world-poster.jpg';
const WA_NUMBER = '51907130225';
const CHAPTER_TRANSITION_START = 0.47;
const CHAPTER_TRANSITION_END = 0.53;
const CHAPTER_TRANSITION_OFFSET_DESKTOP = 220;
const CHAPTER_TRANSITION_OFFSET_MOBILE = 160;
const waLink = (message) => 'https://wa.me/' + WA_NUMBER + '?text=' + encodeURIComponent(message);

const CHAPTERS = [
    {
        id: 'entender', label: 'Entender', eyebrow: 'EL GEM · PREPARACIÓN DE OFERTAS',
        title: <>Cada requisito.<br /><em>En su sitio.</em></>,
        body: 'Prepara expedientes técnicos y económicos con una ruta clara de lectura, control y revisión antes de presentar.',
        action: 'Pedir una demostración',
        proof: 'Una herramienta para trabajar las Bases Integradas con método.'
    },
    {
        id: 'bases', label: 'Bases', eyebrow: '01 · LEER LAS BASES',
        title: <>Del proceso<br />a un <em>mapa claro.</em></>,
        body: 'El Gem convierte los requisitos de las Bases Integradas en un checklist organizado por admisión, calificación y anexos.',
        proof: 'Requisito · documento · anexo'
    },
    {
        id: 'sustento', label: 'Sustento', eyebrow: '02 · ORDENAR EL SUSTENTO',
        title: <>Cada documento<br /><em>tiene un propósito.</em></>,
        body: 'Relaciona los documentos de tu empresa con lo que exige el proceso y detecta pendientes antes del cierre.',
        proof: 'Experiencia · poderes · vigencias'
    },
    {
        id: 'economia', label: 'Economía', eyebrow: '03 · REVISAR LA PROPUESTA',
        title: <>Técnica y economía,<br /><em>en concordancia.</em></>,
        body: 'Revisa la consistencia entre tus análisis de precios unitarios y la carta de propuesta económica.',
        proof: 'APU ↔ carta de propuesta'
    },
    {
        id: 'expediente', label: 'Expediente', eyebrow: '04 · CERRAR EL EXPEDIENTE',
        title: <>Una última revisión.<br /><em>Con todo a la vista.</em></>,
        body: 'Reúne documentos, vigencias y observaciones en una sola ruta de control. Tú conservas la decisión final.',
        action: 'Conversar por WhatsApp',
        proof: 'El Gem SEACE · KREO IA Studio'
    }
];

const processSteps = [
    {
        icon: FileSearch, label: 'Lectura', title: 'Comprende el proceso',
        body: 'Empieza por las Bases Integradas y ubica cada condición que debe atender la oferta.'
    },
    {
        icon: ListChecks, label: 'Estructura', title: 'Ordena los requisitos',
        body: 'Trabaja con un checklist por documentos, anexos y criterios de evaluación.'
    },
    {
        icon: FolderCheck, label: 'Control', title: 'Revisa antes de presentar',
        body: 'Identifica faltantes y verifica la consistencia del expediente técnico y económico.'
    }
];

function clamp(value, min = 0, max = 1) {
    return Math.min(max, Math.max(min, value));
}

export default function LandingPremium() {
    const worldRef = useRef(null);
    const videoRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const world = worldRef.current;
        const video = videoRef.current;
        if (!world || !video) return undefined;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let frame = 0;
        let targetProgress = 0;
        let videoStarted = false;

        const loadVideo = () => {
            if (videoStarted || reduceMotion.matches) return;
            videoStarted = true;
            video.src = VIDEO_SRC;
            video.load();
        };

        const update = () => {
            frame = 0;
            const scrollRange = Math.max(1, world.offsetHeight - window.innerHeight);
            const nextProgress = clamp(-world.getBoundingClientRect().top / scrollRange);
            const chapterPosition = nextProgress * (CHAPTERS.length - 1);
            const nextIndex = Math.min(CHAPTERS.length - 1, Math.round(chapterPosition));
            targetProgress = nextProgress;
            world.style.setProperty('--world-progress', String(nextProgress));
            setProgress((previous) => Math.abs(previous - nextProgress) < 0.003 ? previous : nextProgress);
            setActiveIndex((previous) => previous === nextIndex ? previous : nextIndex);

            if (!reduceMotion.matches && video.readyState >= 1 && Number.isFinite(video.duration) && video.duration > 0 && !video.seeking) {
                const time = Math.min(video.duration - 0.06, Math.max(0, targetProgress * video.duration));
                if (Math.abs(video.currentTime - time) > 0.09) {
                    try { video.currentTime = time; } catch { /* Keep the poster if this decoder cannot seek. */ }
                }
            }
        };

        const requestUpdate = () => {
            if (!frame) frame = window.requestAnimationFrame(update);
        };
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                loadVideo();
                observer.disconnect();
            }
        }, { rootMargin: '100% 0px 100% 0px' });

        observer.observe(world);
        video.addEventListener('loadedmetadata', requestUpdate);
        video.addEventListener('seeked', requestUpdate);
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate, { passive: true });
        requestUpdate();

        return () => {
            observer.disconnect();
            video.removeEventListener('loadedmetadata', requestUpdate);
            video.removeEventListener('seeked', requestUpdate);
            window.removeEventListener('scroll', requestUpdate);
            window.removeEventListener('resize', requestUpdate);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    const goToChapter = (index) => {
        const world = worldRef.current;
        if (!world) return;
        const range = Math.max(0, world.offsetHeight - window.innerHeight);
        const position = world.getBoundingClientRect().top + window.scrollY + range * (index / (CHAPTERS.length - 1));
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: position, behavior: reduced ? 'auto' : 'smooth' });
    };

    const chapterPosition = progress * (CHAPTERS.length - 1);
    const transitionOffsetDistance = window.matchMedia('(max-width: 640px)').matches
        ? CHAPTER_TRANSITION_OFFSET_MOBILE
        : CHAPTER_TRANSITION_OFFSET_DESKTOP;

    return (
        <div className="seace-landing">
            <a className="seace-skip" href="#metodo">Saltar al contenido</a>
            <main>
                <section
                    ref={worldRef}
                    className="seace-world"
                    aria-label="Recorrido por la preparación de un expediente SEACE"
                    style={{ minHeight: (CHAPTERS.length + 1) + '00svh' }}
                >
                    <div className="seace-world__stage">
                        <img className="seace-world__poster" src={POSTER_SRC} alt="" fetchPriority="high" />
                        <video
                            ref={videoRef}
                            className="seace-world__video"
                            poster={POSTER_SRC}
                            muted
                            playsInline
                            preload="none"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                        <div className="seace-world__shade" aria-hidden="true" />

                        <header className="seace-nav">
                            <a className="seace-brand" href="#inicio" aria-label="El Gem SEACE, inicio">
                                <span className="seace-brand__mark"><ShieldCheck size={19} strokeWidth={1.7} /></span>
                                <span className="seace-brand__copy">
                                    <strong>EL GEM</strong>
                                    <small>SEACE · KREO IA</small>
                                </span>
                            </a>
                            <nav className="seace-nav__links" aria-label="Navegación principal">
                                <a href="#metodo">El método</a>
                                <a href="#control">Qué revisa</a>
                                <Link to="/login">Ingresar</Link>
                            </nav>
                            <a className="seace-nav__cta" href={waLink('Hola, quiero una demostración de El Gem SEACE.')} target="_blank" rel="noopener noreferrer">
                                <MessageCircle size={16} />
                                <span>Solicitar demo</span>
                            </a>
                        </header>

                        <div className="seace-world__edition" aria-hidden="true">
                            <span>OFERTAS PÚBLICAS</span>
                            <span>PERÚ · 2026</span>
                        </div>

                        <div className="seace-world__copy" id="inicio" aria-live="polite">
                            {CHAPTERS.map((chapter, index) => {
                                const base = Math.floor(chapterPosition);
                                const segmentProgress = chapterPosition - base;
                                const transitionProgress = clamp(
                                    (segmentProgress - CHAPTER_TRANSITION_START) /
                                    (CHAPTER_TRANSITION_END - CHAPTER_TRANSITION_START)
                                );
                                const opacity = index === base
                                    ? 1 - transitionProgress
                                    : index === base + 1
                                        ? transitionProgress
                                        : 0;
                                const accessible = activeIndex === index;
                                const offset = index === base
                                    ? -transitionOffsetDistance * transitionProgress
                                    : index === base + 1
                                        ? transitionOffsetDistance * (1 - transitionProgress)
                                        : 0;
                                const style = {
                                    opacity,
                                    '--chapter-offset': offset + 'px'
                                };

                                return (
                                    <article className="seace-chapter" key={chapter.id} style={style} aria-hidden={!accessible}>
                                        <div className="seace-chapter__eyebrow">
                                            <span className="seace-chapter__signal" />
                                            {chapter.eyebrow}
                                        </div>
                                        <h1 className="seace-chapter__title">{chapter.title}</h1>
                                        {accessible && (
                                            <div className="seace-chapter__support">
                                                <p className="seace-chapter__body">{chapter.body}</p>
                                                <div className="seace-chapter__actions">
                                                    {chapter.action && (
                                                        <a
                                                            className="seace-button seace-button--copper"
                                                            href={waLink('Hola, quiero una demostración de El Gem SEACE con mis Bases Integradas.')}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            tabIndex={0}
                                                        >
                                                            {chapter.action}<ArrowRight size={17} />
                                                        </a>
                                                    )}
                                                    {index === 0 && (
                                                        <button className="seace-button seace-button--quiet" type="button" onClick={() => goToChapter(1)} tabIndex={0}>
                                                            Ver el recorrido<ArrowDown size={15} />
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="seace-chapter__proof"><Check size={14} />{chapter.proof}</p>
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>

                        <div className="seace-route" aria-label="Capítulos del recorrido">
                            {CHAPTERS.map((chapter, index) => (
                                <button
                                    className={'seace-route__item' + (activeIndex === index ? ' is-active' : '')}
                                    key={chapter.id}
                                    type="button"
                                    onClick={() => goToChapter(index)}
                                    aria-label={'Ir a capítulo ' + (index + 1) + ': ' + chapter.label}
                                    aria-current={activeIndex === index ? 'step' : undefined}
                                    aria-controls="inicio"
                                >
                                    <span className="seace-route__label">{chapter.label}</span>
                                    <span className="seace-route__dot" />
                                </button>
                            ))}
                        </div>

                        <div className="seace-world__footer" aria-hidden="true">
                            <span>DESLIZA PARA RECORRER</span>
                            <span className="seace-world__progress"><i /></span>
                            <span>{String(activeIndex + 1).padStart(2, '0')} / {String(CHAPTERS.length).padStart(2, '0')}</span>
                        </div>
                    </div>
                </section>

                <section className="seace-method" id="metodo">
                    <div className="seace-section-label"><span>EL PROCESO</span><span>01 — 03</span></div>
                    <div className="seace-method__intro">
                        <p className="seace-kicker">CLARIDAD ANTES DEL CIERRE</p>
                        <h2>De las Bases al expediente.<br /><em>Con orden en cada paso.</em></h2>
                        <p className="seace-method__lede">El Gem te ayuda a convertir la convocatoria en una lista de trabajo que puedes revisar, completar y controlar.</p>
                    </div>
                    <div className="seace-steps">
                        {processSteps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <article className="seace-step" key={step.label}>
                                    <div className="seace-step__top">
                                        <span className="seace-step__num">0{index + 1}</span>
                                        <Icon size={20} strokeWidth={1.6} />
                                    </div>
                                    <p className="seace-step__label">{step.label}</p>
                                    <h3>{step.title}</h3>
                                    <p className="seace-step__body">{step.body}</p>
                                </article>
                            );
                        })}
                    </div>
                </section>

                <section className="seace-controls" id="control">
                    <div className="seace-controls__heading">
                        <p className="seace-kicker">LO QUE PUEDES PONER EN ORDEN</p>
                        <h2>Un expediente es más que<br /><em>una carpeta de archivos.</em></h2>
                    </div>
                    <div className="seace-ledger" role="list">
                        <div className="seace-ledger__row" role="listitem">
                            <span className="seace-ledger__icon"><FileCheck2 size={20} /></span>
                            <span className="seace-ledger__name">Admisión y calificación</span>
                            <span className="seace-ledger__detail">Requisitos y anexos de las Bases</span>
                            <CheckCircle2 size={17} className="seace-ledger__check" />
                        </div>
                        <div className="seace-ledger__row" role="listitem">
                            <span className="seace-ledger__icon"><ShieldCheck size={20} /></span>
                            <span className="seace-ledger__name">Documentos del postor</span>
                            <span className="seace-ledger__detail">Vigencias, RUC y acreditaciones</span>
                            <CheckCircle2 size={17} className="seace-ledger__check" />
                        </div>
                        <div className="seace-ledger__row" role="listitem">
                            <span className="seace-ledger__icon"><ListChecks size={20} /></span>
                            <span className="seace-ledger__name">Oferta técnica</span>
                            <span className="seace-ledger__detail">Sustento frente a cada criterio</span>
                            <CheckCircle2 size={17} className="seace-ledger__check" />
                        </div>
                        <div className="seace-ledger__row" role="listitem">
                            <span className="seace-ledger__icon"><FolderCheck size={20} /></span>
                            <span className="seace-ledger__name">Oferta económica</span>
                            <span className="seace-ledger__detail">APU y carta de propuesta</span>
                            <CheckCircle2 size={17} className="seace-ledger__check" />
                        </div>
                    </div>
                    <p className="seace-controls__note">El Gem es una herramienta de apoyo documental. La revisión final y la presentación corresponden al postor.</p>
                </section>

                <section className="seace-final" id="demo">
                    <div className="seace-final__stamp"><ShieldCheck size={18} /> EL GEM · KREO IA STUDIO</div>
                    <p className="seace-kicker">TU PRÓXIMO CIERRE MERECE MÉTODO</p>
                    <h2>Prepara tu siguiente<br /><em>oferta con claridad.</em></h2>
                    <p>Conoce El Gem SEACE con tus propias Bases Integradas.</p>
                    <a className="seace-button seace-button--copper" href={waLink('Hola, quiero una demostración de El Gem SEACE con mis Bases Integradas.')} target="_blank" rel="noopener noreferrer">
                        <MessageCircle size={17} /> Conversar por WhatsApp <ArrowRight size={17} />
                    </a>
                </section>
            </main>

            <footer className="seace-footer">
                <a className="seace-brand seace-brand--footer" href="#inicio">
                    <span className="seace-brand__mark"><ShieldCheck size={18} strokeWidth={1.7} /></span>
                    <span className="seace-brand__copy"><strong>EL GEM</strong><small>SEACE · KREO IA</small></span>
                </a>
                <span>© 2026 KREO IA Studio</span>
                <a href={waLink('Hola, quiero información sobre El Gem SEACE.')} target="_blank" rel="noopener noreferrer">+51 907 130 225</a>
            </footer>
        </div>
    );
}


